import React, { FC, ReactNode, useState } from 'react';
import styled from '@emotion/styled';
import SVG from '@app/components/SVG';
import ClearIcon from '@app/images/ic_close.svg';
import Button from '@app/components/Button';
import Card from '@app/components/card/Card';
import { BodyRegularStartTransparentBlack600, Bold13TransparentBlack900, H5StartTransparentBlack900 } from '@app/components/Text';
import SendValidationEmailToCustomerForm, {
  SendValidationEmailToCustomerFormFields,
} from '@mortee/routes/accountVerification/sendValidationEmailToCustomer/SendValidationEmailToCustomerForm';
import useLoadable from '@app/hooks/loadable/useLoadable';
import TransitionLoader from '@app/components/TransitionLoader';
import { css } from '@emotion/css';
import { getValidationEmailToCustomerValues, sendValidationEmailToCustomer } from '@mortee/services/accountVerificationService';
import Loader from '@app/components/Loader';
import SendValidationEmailToCustomerSummary from '@mortee/routes/accountVerification/sendValidationEmailToCustomer/SendValidationEmailToCustomerSummary';
import { shoot } from '@app/utils/messageLauncher';
import {
  transformToCustomerValidationEmailValues,
  ValidationEmailToCustomerValues,
} from '@mortee/domain/morteeAccountVerification';

interface Props {
  validatedPayeeUniformId: string;
  validatedPayeeAccountUniformId: string;
  onDone(): void;
}

interface FormViewState {
  view: 'form';
  formValues: SendValidationEmailToCustomerFormFields | null;
}

interface SummaryViewState {
  view: 'summary';
  formValues: SendValidationEmailToCustomerFormFields;
}

type ViewState = FormViewState | SummaryViewState;
const viewStatesOrder: ViewState['view'][] = ['form', 'summary'];

const SendValidationEmailToCustomerModal: FC<Props> = (props) => {
  const { validatedPayeeUniformId, validatedPayeeAccountUniformId, onDone } = props;

  const [isSendingEmailInProgress, setIsSendingEmailInProgress] = useState<boolean>(false);
  const [viewState, setViewState] = useState<ViewState>({ view: 'form', formValues: null });

  const [emailValues] = useLoadable(async (): Promise<ValidationEmailToCustomerValues> => {
    const serverResponse = await getValidationEmailToCustomerValues(validatedPayeeUniformId, validatedPayeeAccountUniformId);
    return transformToCustomerValidationEmailValues(serverResponse);
  }, [validatedPayeeUniformId, validatedPayeeAccountUniformId]);

  const onFormFilled = (formValues: SendValidationEmailToCustomerFormFields): void => {
    setViewState({ view: 'summary', formValues: formValues });
  };

  const onSummaryBack = (): void => {
    setViewState({ view: 'form', formValues: viewState.formValues });
  };

  const onSendEmail = async (values: SendValidationEmailToCustomerFormFields): Promise<void> => {
    setIsSendingEmailInProgress(true);

    try {
      await sendValidationEmailToCustomer(validatedPayeeUniformId, validatedPayeeAccountUniformId, values);
      shoot({ type: 'success', closeable: true }, 'Email sent to customer');
      onDone();
    } finally {
      setIsSendingEmailInProgress(false);
    }
  };

  return (
    <Card id='send-validation-email-to-customer-modal'>
      <TransitionLoader loading={isSendingEmailInProgress} contentClassName={modalContentCss}>
        <TitleLine>
          <Title>
            <Bold13TransparentBlack900.div>
              Step {viewStatesOrder.indexOf(viewState.view) + 1} of {viewStatesOrder.length}
            </Bold13TransparentBlack900.div>
            <H5StartTransparentBlack900.div>Send Email to Customer</H5StartTransparentBlack900.div>
          </Title>
          <CloseButton
            id='btn-send-validation-email-to-customer-modal-close'
            appearance='text'
            colorScheme='primary'
            onClick={onDone}
          >
            <SVG accessibilityLabel='close' image={ClearIcon} height={20} />
          </CloseButton>
        </TitleLine>
        {viewState.view === 'form' ? (
          emailValues.resolve<ReactNode>(
            (loadedEmailValues) => (
              <StyledSendValidationEmailToCustomerForm
                initialFormValues={viewState.formValues ?? loadedEmailValues}
                onFormSubmit={onFormFilled}
              />
            ),
            () => <Loader spinning />,
            () => (
              <BodyRegularStartTransparentBlack600.div>
                Failed to load data required for this process
              </BodyRegularStartTransparentBlack600.div>
            ),
          )
        ) : (
          <StyledSendValidationEmailToCustomerSummary values={viewState.formValues} onOk={onSendEmail} onBack={onSummaryBack} />
        )}
      </TransitionLoader>
    </Card>
  );
};

export default SendValidationEmailToCustomerModal;

const modalContentCss = css`
  width: min(600px, 90vw);
  min-height: 400px;
  max-height: 90vh;

  padding: 26px 32px 32px;
  position: relative;

  display: flex;
  flex-direction: column;
`;

const TitleLine = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  flex: 0 0 auto;

  margin-bottom: 4px;
`;

const Title = styled.div`
  & > *:not(:first-child) {
    margin-top: 8px;
  }
`;

const CloseButton = styled(Button)`
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  transition: 0.1s all ease-in-out;
  padding: 4px 6px 5px;
  margin-bottom: 3px;
`;

const StyledSendValidationEmailToCustomerForm = styled(SendValidationEmailToCustomerForm)`
  flex: 1;
  padding-top: 20px;
`;

const StyledSendValidationEmailToCustomerSummary = styled(SendValidationEmailToCustomerSummary)`
  flex: 1;
`;
